import { cloneDeep } from 'lodash';
import { orderStage, instantBookOrderStage, bookingJourneyType } from 'common/constants/booking';

const milestoneStatus = {
  TODO: 'todo',
  DOING: 'doing',
  DONE: 'done',
  FAILED: 'failed',
};

// mileStones show in UI
const mileStones = {
  CREATE: 'create',
  CONFIRMATION: 'confirmation',
  PAYMENT: 'payment',
  CHECK_IN: 'check_in',
  COMPLETED: 'completed',
  // instant book
  FILLING_PERSONAL_INFO: 'filling_personal_info',
  SIGNING_AGREEMENT: 'signing_agreement',
  MAKING_PAYMENT: 'making_payment',
  BOOKING_CONFIRMED: 'booking_confirmed',
  CHECKED_IN: 'checked_in',
  LOST: 'lost',
  REFUNDING: 'refunding',
  CANCELLED: 'cancelled',
  SELECTING_PAYMENT_PLAN: 'selecting_payment_plan',
};

// orderStage和milestone的对应关系
// orderStages中的LOST、CANCELLED、AFTER_SALES_INTERVENTION、REFUNDING
// 需要根据secondaryStages或前面的stage流程来确定是哪一个milestone失败的;
// 然后在对应的milestone会显示红色代表失败
const stageMilestoneMapping = {
  [orderStage.WAITING_FOR_CONFIRMATION]: mileStones.CREATE,
  [orderStage.WAITING_FOR_PAYMENT]: mileStones.CONFIRMATION,
  [orderStage.WAITING_FOR_CHECK_IN]: mileStones.PAYMENT,
  [orderStage.CHECKED_IN]: mileStones.CHECK_IN,
  [orderStage.COMPLETED]: mileStones.COMPLETED,
  [instantBookOrderStage.FILLING_PERSONAL_INFO]: mileStones.FILLING_PERSONAL_INFO,
  [instantBookOrderStage.SIGNING_AGREEMENT]: mileStones.SIGNING_AGREEMENT,
  [instantBookOrderStage.MAKING_PAYMENT]: mileStones.MAKING_PAYMENT,
  [instantBookOrderStage.BOOKING_CONFIRMED]: mileStones.BOOKING_CONFIRMED,
};

const noPaymentMilesStoneMapping = {
  [orderStage.WAITING_FOR_CONFIRMATION]: mileStones.CREATE,
  [orderStage.WAITING_FOR_PAYMENT]: mileStones.CONFIRMATION,
  [orderStage.WAITING_FOR_CHECK_IN]: mileStones.PAYMENT,
  [orderStage.CHECKED_IN]: mileStones.CHECK_IN,
  [orderStage.COMPLETED]: mileStones.COMPLETED,
  [instantBookOrderStage.FILLING_PERSONAL_INFO]: mileStones.FILLING_PERSONAL_INFO,
  [instantBookOrderStage.SIGNING_AGREEMENT]: mileStones.SIGNING_AGREEMENT,
  [instantBookOrderStage.SELECTING_PAYMENT_PLAN]: mileStones.SELECTING_PAYMENT_PLAN,
  [instantBookOrderStage.BOOKING_CONFIRMED]: mileStones.BOOKING_CONFIRMED,
};
const commonMilestoneList = [
  mileStones.CREATE,
  mileStones.CONFIRMATION,
  mileStones.PAYMENT,
  mileStones.CHECK_IN,
  mileStones.COMPLETED,
];

const instantMilestoneList = [
  mileStones.FILLING_PERSONAL_INFO,
  mileStones.SIGNING_AGREEMENT,
  mileStones.MAKING_PAYMENT,
  mileStones.BOOKING_CONFIRMED,
  mileStones.COMPLETED,
  mileStones.CHECKED_IN,
];

const noPaymentMileStoneList = [
  mileStones.FILLING_PERSONAL_INFO,
  mileStones.SIGNING_AGREEMENT,
  mileStones.SELECTING_PAYMENT_PLAN,
  mileStones.MAKING_PAYMENT,
  mileStones.BOOKING_CONFIRMED,
  mileStones.COMPLETED,
  mileStones.CHECKED_IN,
];
const commonMilestoneDetailList = {
  [mileStones.CREATE]: { status: milestoneStatus.TODO, iconHash: 'progress-application-form' },
  [mileStones.CONFIRMATION]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-landlord-pre-approval',
  },
  [mileStones.PAYMENT]: { status: milestoneStatus.TODO, iconHash: 'progress-deposit-payment' },
  [mileStones.CHECK_IN]: {
    status: milestoneStatus.TODO,
    iconHash: 'longtail-process-check-in',
  },
  [mileStones.COMPLETED]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-booking-complete',
  },
};

const instantMilestoneDetailList = {
  [mileStones.FILLING_PERSONAL_INFO]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-fill-personal-info',
  },
  [mileStones.SIGNING_AGREEMENT]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-sign-contract',
  },
  [mileStones.MAKING_PAYMENT]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-making-payment',
  },
  [mileStones.BOOKING_CONFIRMED]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-booking-confirmed',
  },
  [mileStones.COMPLETED]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-booking-complete',
  },
  [mileStones.CHECKED_IN]: {
    status: milestoneStatus.TODO,
    iconHash: 'longtail-process-check-in',
  },
};
const instantNoPaymentMilestoneDetailList = {
  [mileStones.FILLING_PERSONAL_INFO]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-fill-personal-info',
  },
  [mileStones.SIGNING_AGREEMENT]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-sign-contract',
  },
  [mileStones.SELECTING_PAYMENT_PLAN]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-selecting-payment-plan',
  },
  [mileStones.MAKING_PAYMENT]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-making-payment',
  },
  [mileStones.BOOKING_CONFIRMED]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-booking-confirmed',
  },
  [mileStones.COMPLETED]: {
    status: milestoneStatus.TODO,
    iconHash: 'progress-booking-complete',
  },
  [mileStones.CHECKED_IN]: {
    status: milestoneStatus.TODO,
    iconHash: 'longtail-process-check-in',
  },
};
const useInstantProcesses = ({
  processes = [],
  bookingJourney,
  stage,
  isNoPayment = false,
  landlordConfirmedPayment,
}) => {
  const isInstantBook = bookingJourney === bookingJourneyType.INSTANT_BOOK;

  const milestoneList = cloneDeep(
    isInstantBook
      ? isNoPayment
        ? noPaymentMileStoneList
        : instantMilestoneList
      : commonMilestoneList,
  );

  let milestoneDetailList = cloneDeep(
    isInstantBook
      ? isNoPayment
        ? instantNoPaymentMilestoneDetailList
        : instantMilestoneDetailList
      : commonMilestoneDetailList,
  );

  const getMileStoneDetails = () => {
    let completeIndex = -1;

    const refundingTarget = processes.find(
      process => process.currentStage === instantBookOrderStage.REFUNDING,
    );
    const canceledTarget = processes.find(
      process => process.currentStage === instantBookOrderStage.CANCELLED,
    );

    const noPaymentConfirmTarget = isNoPayment
      ? processes.find(process => process.currentStage === instantBookOrderStage.BOOKING_CONFIRMED)
      : null;

    if (refundingTarget) {
      milestoneDetailList = {
        [mileStones.REFUNDING]: {
          status: milestoneStatus.DOING,
          type: 'refund',
          updatedAt: refundingTarget.updatedAt,
          iconHash: 'progress-refunding',
          secondaryStage: 'refund_pending',
        },
        [mileStones.CANCELLED]: {
          status: milestoneStatus.TODO,
          iconHash: 'progress-instant-book-cancelled',
        },
      };
    }
    if (isNoPayment && canceledTarget) {
      milestoneDetailList = {
        [mileStones.CANCELLED]: {
          iconHash: 'progress-instant-book-cancelled',
          status: milestoneStatus.DONE,
          type: 'cancel',
          updatedAt: canceledTarget.updatedAt,
          secondaryStage: canceledTarget.currentSecondaryStage?.toLowerCase(),
        },
      };
    }
    if (!isNoPayment && canceledTarget) {
      milestoneDetailList[mileStones.REFUNDING].status = milestoneStatus.DONE;
      milestoneDetailList[mileStones.REFUNDING].secondaryStage = 'refund_refunded';

      Object.assign(milestoneDetailList[mileStones.CANCELLED], {
        status: milestoneStatus.DONE,
        type: 'cancel',
        updatedAt: canceledTarget.updatedAt,
        secondaryStage: canceledTarget.currentSecondaryStage?.toLowerCase(),
      });
    }

    if (noPaymentConfirmTarget && !canceledTarget) {
      milestoneDetailList[mileStones.SELECTING_PAYMENT_PLAN].status = milestoneStatus.DONE;
      milestoneDetailList[mileStones.SELECTING_PAYMENT_PLAN].updatedAt =
        noPaymentConfirmTarget.updatedAt;
    }

    if (
      isNoPayment &&
      [instantBookOrderStage.COMPLETED, instantBookOrderStage.BOOKING_CONFIRMED].includes(stage) &&
      !landlordConfirmedPayment
    ) {
      milestoneDetailList[mileStones.MAKING_PAYMENT].status = milestoneStatus.DOING;
      milestoneDetailList[mileStones.BOOKING_CONFIRMED].status = milestoneStatus.TODO;
      milestoneDetailList[mileStones.COMPLETED].status = milestoneStatus.TODO;
    }
    processes.forEach(process => {
      const { currentStage, updatedAt, currentSecondaryStage } = process;
      const nextMileStone = milestoneList[completeIndex + 1];

      if (
        isNoPayment &&
        [instantBookOrderStage.COMPLETED, instantBookOrderStage.BOOKING_CONFIRMED].includes(
          currentStage,
        ) &&
        !canceledTarget &&
        !refundingTarget
      ) {
        if (!landlordConfirmedPayment) {
          Object.assign(milestoneDetailList?.[mileStones.MAKING_PAYMENT], {
            status: milestoneStatus.DOING,
            updatedAt,
            secondaryStage: currentSecondaryStage?.toLowerCase(),
          });
        } else if (currentStage === instantBookOrderStage.BOOKING_CONFIRMED) {
          Object.assign(milestoneDetailList[mileStones.BOOKING_CONFIRMED], {
            status: milestoneStatus.DONE,
            updatedAt,
            secondaryStage: currentSecondaryStage?.toLowerCase(),
          });
          Object.assign(milestoneDetailList?.[mileStones.MAKING_PAYMENT], {
            status: milestoneStatus.DONE,
            updatedAt,
            secondaryStage: currentSecondaryStage?.toLowerCase(),
          });
        } else if (currentStage === instantBookOrderStage.COMPLETED) {
          Object.assign(milestoneDetailList[mileStones.MAKING_PAYMENT], {
            status: milestoneStatus.DONE,
            updatedAt,
            secondaryStage: currentSecondaryStage?.toLowerCase(),
          });
          Object.assign(milestoneDetailList[mileStones.COMPLETED], {
            status: milestoneStatus.DONE,
            updatedAt,
            secondaryStage: currentSecondaryStage?.toLowerCase(),
          });
        }
        return;
      } else if (milestoneDetailList[nextMileStone]) {
        if (
          isNoPayment
            ? noPaymentMilesStoneMapping[currentStage] === nextMileStone
            : stageMilestoneMapping[currentStage] === nextMileStone
        ) {
          Object.assign(milestoneDetailList[nextMileStone], {
            status: milestoneStatus.DONE,
            updatedAt,
            secondaryStage: currentSecondaryStage?.toLowerCase(),
          });
          completeIndex += 1;
        } else {
          if (isInstantBook) {
            if (currentStage === instantBookOrderStage.LOST) {
              const preMilestone = milestoneList[completeIndex];

              milestoneDetailList[mileStones.LOST] = {
                status: milestoneStatus.DONE,
                updatedAt,
                type: 'lost',
                iconHash: 'progress-instant-book-lost',
                secondaryStage: currentSecondaryStage?.toLowerCase(),
              };

              Object.assign(milestoneDetailList[preMilestone], {
                border: 'dotted',
                iconHash: 'progress-stop',
              });

              while (milestoneList[completeIndex + 1]) {
                delete milestoneDetailList[milestoneList[completeIndex + 1]];
                // milestoneList.splice(completeIndex, 1);

                completeIndex += 1;
              }
            }
          } else if (currentStage === orderStage.WAITING_FOR_CONFIRMATION) {
            Object.assign(milestoneDetailList[mileStones.CONFIRMATION], {
              updatedAt,
              secondaryStage: currentSecondaryStage?.toLowerCase(),
            });
          } else {
            Object.assign(milestoneDetailList[nextMileStone], {
              status: milestoneStatus.FAILED,
              updatedAt,
            });
          }
        }
      }
    });

    return milestoneDetailList;
  };

  return {
    milestoneDetails: getMileStoneDetails(),
  };
};

export default useInstantProcesses;
